/**
 * App.tsx — Root-Komponente des Potree Viewers
 *
 * Auth Gate: Login → Projektauswahl → Viewer
 * Der Potree-Viewer (index.html #viewer-shell) wird erst sichtbar,
 * wenn der User eingeloggt ist und ein Projekt gewählt hat.
 */

import { useEffect } from 'react'
import './components/SemanticZoomOverlay.css'
import { AppShell } from './components/layout/AppShell'
import { LoginPage } from './components/auth/LoginPage'
import { ProjectSelector } from './components/auth/ProjectSelector'
import { CssDevEditor } from './components/dev/CssDevEditor'
import { PresentationDock } from './components/presentation/PresentationDock'
import { ControllerPage } from './components/controller/ControllerPage'
import { GameControllerSession } from './components/controller/GameControllerSession'
import { useAuthStore } from './store/auth'
import { useControllerStore } from './store/controller'
import { useProjectLiveUpdates } from './hooks/useProjectLiveUpdates'

/** Zeigt/versteckt den nativen Viewer-Shell (Potree + Dock + StatusBar) */
function setViewerVisible(visible: boolean) {
  const shell = document.getElementById('viewer-shell')
  if (shell) shell.style.display = visible ? '' : 'none'
}

export function App() {
  // Standalone-Route: Mobile Controller (öffnet sich nach QR-Scan).
  // Wir nutzen einen Query-Parameter statt einer Sub-Route, weil der
  // Static-Host für unbekannte Pfade 404 liefert; Root liefert immer
  // index.html aus.
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    if (params.get('ctrl') === '1' || window.location.pathname === '/controller') {
      // #react-root hat pointer-events:none → ohne Wrapper landet kein Touch
      // bei der ControllerPage. Außerdem über den Viewer-Schichten platzieren.
      return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 999999, pointerEvents: 'all' }}>
          <ControllerPage />
        </div>
      )
    }
  }

  const { isAuthenticated, isLoading, currentProjectId, currentProject, loadUser } = useAuthStore()

  // Controller-Session lebt auf App-Level, damit sie über Projektwechsel
  // hinweg erhalten bleibt (kein erneutes QR-Scannen nötig).
  const controllerOpen = useControllerStore((s) => s.open)
  const setControllerOpen = useControllerStore((s) => s.setOpen)

  // WS-Verbindung zum aktuellen Projekt; Admin-Änderungen (Pointcloud
  // assigned/unassigned) triggern automatisches Neuladen.
  useProjectLiveUpdates()

  useEffect(() => {
    // Access-Token aus URL-Param übernehmen (?accessToken=oat_…),
    // dann als accessToken in localStorage hinterlegen und URL aufräumen.
    try {
      const params = new URLSearchParams(window.location.search)
      const t = params.get('accessToken')
      if (t) {
        localStorage.setItem('accessToken', t)
        localStorage.removeItem('refreshToken')
        params.delete('accessToken')
        const qs = params.toString()
        const newUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash
        window.history.replaceState({}, '', newUrl)
      }
    } catch { /* ignore */ }
    loadUser()
  }, [loadUser])

  // Viewer zeigen + Projektdaten an script.js übergeben
  useEffect(() => {
    const show = isAuthenticated && !!currentProjectId && !!currentProject
    setViewerVisible(show)
    if (show) {
      // Potree Renderer auf korrekte Größe setzen
      if (typeof (window as any).initPotreeViewer === 'function') {
        (window as any).initPotreeViewer()
      }
      // Projektdaten an script.js senden → lädt Pointclouds aus der API
      window.dispatchEvent(new CustomEvent('orto:loadProject', { detail: currentProject }))
    }
  }, [isAuthenticated, currentProjectId, currentProject])

  // Loading state — fullscreen, blockiert Interaktion
  if (isLoading) {
    return (
      <>
        <div className="loading-screen">
          <img className="loading-screen__logo" src="/assets/logo_black.svg" alt="orto" />
        </div>
        <CssDevEditor />
      </>
    )
  }

  // Not authenticated → Login (fullscreen, blockiert alles)
  if (!isAuthenticated) {
    return (
      <>
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, pointerEvents: 'all' }}>
          <LoginPage />
        </div>
        <CssDevEditor />
      </>
    )
  }

  // Authenticated but no project selected → Project selector (fullscreen)
  if (!currentProjectId) {
    return (
      <>
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, pointerEvents: 'all' }}>
          <ProjectSelector />
        </div>
        <PresentationDock projectId={null} />
        {controllerOpen && (
          <GameControllerSession onClose={() => setControllerOpen(false)} />
        )}
        <CssDevEditor />
      </>
    )
  }

  // Authenticated and project selected → AppShell (enthält eigenen CssDevEditor)
  return (
    <>
      <AppShell />
      {controllerOpen && (
        <GameControllerSession onClose={() => setControllerOpen(false)} />
      )}
    </>
  )
}
